import { z } from 'zod';
import { dueDateSchema, moneyMinorSchema } from '../api';
import { BLOOD_COMPONENT, BLOOD_GROUP, BLOOD_UNIT_STATUS, COMPLAINT_STATUS, CROSSMATCH_RECORDED_RESULT, DIET_TYPE, HOUSEKEEPING_STATUS, HOUSEKEEPING_TYPE, TASK_STATUS, } from '../enums';
// ── Blood bank (SRS 6.9) ─────────────────────────────────────────────────────
export const upsertDonorSchema = z.object({
    name: z.string().trim().min(2).max(160),
    cnic: z.string().trim().max(20).optional(),
    phone: z.string().trim().max(20).optional(),
    bloodGroup: z.enum(BLOOD_GROUP),
    /** Screening questionnaire answers, kept as given so the form can evolve. */
    screening: z.record(z.string(), z.union([z.string(), z.boolean()])).default({}),
});
export const collectBloodSchema = z.object({
    donorId: z.string().uuid(),
    component: z.enum(BLOOD_COMPONENT).default('whole_blood'),
    /** Shelf life differs by component; the caller may override the default. */
    shelfLifeDays: z.number().int().min(1).max(3650).optional(),
    collectedAt: z.string().datetime().optional(),
    notes: z.string().max(600).optional(),
});
export const screenUnitSchema = z.object({
    /** Transfusion-transmissible infection screen; all must be negative. */
    hiv: z.boolean(),
    hepatitisB: z.boolean(),
    hepatitisC: z.boolean(),
    syphilis: z.boolean(),
    malaria: z.boolean(),
    notes: z.string().max(600).optional(),
});
export const crossMatchRequestSchema = z.object({
    patientId: z.string().uuid(),
    admissionId: z.string().uuid().nullable().optional(),
    bloodUnitId: z.string().uuid(),
    notes: z.string().max(600).optional(),
});
export const crossMatchResultSchema = z.object({
    result: z.enum(CROSSMATCH_RECORDED_RESULT),
    notes: z.string().max(600).optional(),
});
export const transfusionSchema = z.object({
    transfusedAt: z.string().datetime().optional(),
    /** A reaction is recorded against the unit, not just the patient note. */
    reactionNote: z.string().max(2000).optional(),
});
export const bloodStockQuerySchema = z.object({
    bloodGroup: z.enum(BLOOD_GROUP).optional(),
    component: z.enum(BLOOD_COMPONENT).optional(),
    status: z.enum(BLOOD_UNIT_STATUS).optional(),
});
// ── Ambulance (SRS 10.6) ─────────────────────────────────────────────────────
export const upsertVehicleSchema = z.object({
    registrationNo: z.string().trim().toUpperCase().min(3).max(24),
    type: z.enum(['ambulance', 'mortuary_van', 'utility']).default('ambulance'),
    driverName: z.string().trim().max(160).optional(),
    driverPhone: z.string().trim().max(20).optional(),
    branchId: z.string().uuid().nullable().optional(),
    isActive: z.boolean().default(true),
});
export const startTripSchema = z.object({
    vehicleId: z.string().uuid(),
    patientId: z.string().uuid().nullable().optional(),
    pickup: z.string().trim().min(2).max(300),
    dropoff: z.string().trim().min(2).max(300),
    notes: z.string().max(600).optional(),
});
export const endTripSchema = z.object({
    distanceKm: z.number().min(0).max(5000),
    chargeItemId: z.string().uuid().nullable().optional(),
    /** Overrides the charge master when the trip is quoted by hand. */
    chargeMinor: moneyMinorSchema.min(0).optional(),
    /** Bills the trip to the patient rather than leaving it as a log entry. */
    bill: z.boolean().default(false),
    notes: z.string().max(600).optional(),
});
// ── Dietary (SRS 10.7) ───────────────────────────────────────────────────────
export const dietOrderSchema = z.object({
    admissionId: z.string().uuid(),
    dietType: z.enum(DIET_TYPE),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    notes: z.string().max(600).optional(),
});
// ── Housekeeping (SRS 10.8) ──────────────────────────────────────────────────
export const housekeepingTaskSchema = z.object({
    wardId: z.string().uuid().nullable().optional(),
    bedId: z.string().uuid().nullable().optional(),
    type: z.enum(HOUSEKEEPING_TYPE).default('bed_cleaning'),
    assignedTo: z.string().uuid().nullable().optional(),
});
export const housekeepingUpdateSchema = z.object({
    status: z.enum(HOUSEKEEPING_STATUS),
    assignedTo: z.string().uuid().nullable().optional(),
});
// ── Feedback and complaints (SRS 10.9) ───────────────────────────────────────
export const feedbackSchema = z.object({
    patientId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().nullable().optional(),
    source: z.enum(['qr', 'sms', 'portal', 'kiosk', 'desk']).default('qr'),
    /** Net promoter score, 0–10. */
    npsScore: z.number().int().min(0).max(10).nullable().optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    department: z.string().trim().max(120).optional(),
    comments: z.string().max(4000).optional(),
});
export const complaintSchema = z.object({
    patientId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().nullable().optional(),
    subject: z.string().trim().min(3).max(240),
    body: z.string().trim().min(3).max(8000),
    departmentId: z.string().uuid().nullable().optional(),
    /** Hours until the SLA is breached; the default matches the SRS' 48 hours. */
    slaHours: z.number().int().min(1).max(720).default(48),
});
export const complaintUpdateSchema = z.object({
    status: z.enum(COMPLAINT_STATUS),
    assignedTo: z.string().uuid().nullable().optional(),
    resolution: z.string().max(4000).optional(),
});
// ── Tasks (SRS 10.10) ────────────────────────────────────────────────────────
export const taskSchema = z.object({
    assignedTo: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().nullable().optional(),
    type: z.enum(['general', 'transport', 'sample_collection', 'housekeeping', 'maintenance', 'follow_up']).default('general'),
    title: z.string().trim().min(3).max(240),
    description: z.string().max(4000).optional(),
    refType: z.string().trim().max(60).optional(),
    refId: z.string().trim().max(80).optional(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
    dueAt: dueDateSchema.nullable().optional(),
});
export const taskUpdateSchema = z.object({
    status: z.enum(TASK_STATUS),
    assignedTo: z.string().uuid().nullable().optional(),
    note: z.string().max(2000).optional(),
});
//# sourceMappingURL=support.js.map